/*!
 * SkyPlus Analytics Service
 * Thin client for GET /api/wf/analytics/summary.
 *
 * Mirrors the other SkyPlus services exactly: per-request Bearer headers
 * (no global HTTP interceptor) and base URL from the `skyplus.apiUrl`
 * app-config key. On any error it resolves to an empty summary so the
 * dashboard degrades gracefully rather than throwing.
 */
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { AuthenticationService, AppConfigService } from '@alfresco/adf-core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

/** One labelled count, e.g. { label: 'Mail management', value: 8 }. */
export interface AnalyticsLabelValue {
  label: string;
  value: number;
}

/** One day bucket in the completion trend. `date` is ISO `yyyy-MM-dd`. */
export interface AnalyticsDayValue {
  date: string;
  value: number;
}

/** Full payload from /api/wf/analytics/summary — mirrors WfAnalyticsDtos.AnalyticsSummary. */
export interface AnalyticsSummary {
  days: number;
  tasksByWorkflow: AnalyticsLabelValue[];
  completionsByDay: AnalyticsDayValue[];
  totalCompleted: number;
  overdueCount: number;
  avgCycleDays: number;
  myPendingTasks: number;
  myClaimableTasks: number;
  completedToday: number;
  myCompleted: number;
}

/** One task the caller has completed — behind the dashboard "Completed" card. Mirrors the
 *  backend WfAnalyticsDtos.MyCompletedTask. */
export interface MyCompletedTask {
  instanceId: number | null;
  processInstanceId: string | null;
  wfKey: string | null;
  businessKey: string | null;
  title: string | null;
  taskId: string | null;
  taskName: string | null;
  outcome: string | null;
  completedAt: string | null;
}

/** A dashboard "Start workflow" launcher derived from an active WfDefinition. */
export interface WorkflowLauncher {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  route: string;
}

/** Subset of the backend DefinitionDto we actually use here. */
interface WfDefinitionLite {
  wfKey: string;
  displayName: string;
  description?: string;
  icon?: string;
  color?: string;
  active?: boolean;
  menuVisible?: boolean;
}

@Injectable({ providedIn: 'root' })
export class SkyPlusAnalyticsService {

  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthenticationService);
  private readonly appConfig = inject(AppConfigService);
  private readonly base: string;
  private readonly wfBase: string;

  constructor() {
    const apiUrl = this.appConfig.get<string>('skyplus.apiUrl', '/bpm/api');
    this.wfBase = `${apiUrl}/wf`;
    this.base = `${this.wfBase}/analytics`;
  }

  /** Fetch the dashboard analytics for a trend window of 7, 14 or 30 days. */
  getSummary(days: number): Observable<AnalyticsSummary> {
    const params = new HttpParams().set('days', String(days));
    return this.http
      .get<AnalyticsSummary>(`${this.base}/summary`, { headers: this.authHeaders(), params })
      .pipe(catchError(() => of(this.empty(days))));
  }

  /** The caller's completed tasks (most recent first) — the "Completed" card drill-down. */
  getMyCompleted(limit = 100): Observable<MyCompletedTask[]> {
    const params = new HttpParams().set('limit', String(limit));
    return this.http
      .get<MyCompletedTask[]>(`${this.base}/my-completed`, { headers: this.authHeaders(), params })
      .pipe(catchError(() => of([] as MyCompletedTask[])));
  }

  /**
   * Active workflow definitions mapped to "Start workflow" launchers. Drives the
   * dashboard launcher dynamically so disabled workflows drop off and new ones
   * appear automatically. All launchers route to the Workflows tab.
   */
  getActiveWorkflows(): Observable<WorkflowLauncher[]> {
    return this.http
      .get<WfDefinitionLite[]>(`${this.wfBase}/definitions`, { headers: this.authHeaders() })
      .pipe(
        map((defs) => (defs || [])
          .filter(d => d && d.active !== false && d.menuVisible !== false)
          .map(d => ({
            id: d.wfKey,
            name: d.displayName || d.wfKey,
            description: d.description || 'Open in the Workflows tab',
            icon: d.icon || 'play_circle',
            color: d.color || '#244062',
            route: '/workflow'
          }))),
        catchError(() => of([] as WorkflowLauncher[]))
      );
  }

  private empty(days: number): AnalyticsSummary {
    return {
      days,
      tasksByWorkflow: [],
      completionsByDay: [],
      totalCompleted: 0,
      overdueCount: 0,
      avgCycleDays: 0,
      myPendingTasks: 0,
      myClaimableTasks: 0,
      completedToday: 0,
      myCompleted: 0
    };
  }

  private authHeaders(): HttpHeaders {
    const token = this.auth.getToken();
    return token
      ? new HttpHeaders({ 'Authorization': `Bearer ${token}` })
      : new HttpHeaders();
  }
}